import ArrowDownwardIcon from "@mui/icons-material/ArrowDownward";
import ArrowUpwardIcon from "@mui/icons-material/ArrowUpward";
import UnfoldMoreIcon from "@mui/icons-material/UnfoldMore";
import Box from "@mui/material/Box";
import Table from "@mui/material/Table";
import TableBody from "@mui/material/TableBody";
import TableCell from "@mui/material/TableCell";
import TableHead from "@mui/material/TableHead";
import TableRow from "@mui/material/TableRow";
import Typography from "@mui/material/Typography";
// import ColorPicker from "common/components/Elements/ColorPicker/ColorPicker";
import ExpandableText from "common/components/Elements/ExpandableText/ExpandableText";
import Icon from "common/components/Elements/Icon/Icon";
import type { Color } from "common/utilities/colors/colors.types";
import { deepClone } from "common/utilities/core/object";
import Pagination from "core/components/Elements/Table/Pagination/Pagination";
import { paginatedItems } from "core/utilities/table/table";
import { useEffect, useState } from "react";
import type { FeatureDefinitions, SequenceFeatureWithRef } from "sequenceAnalyser/utilities/sequenceAnalyser.types";
// import { ColorSources } from "sequenceAnalyser/utilities/sequenceAnalyser.types";
import { buildFeatureId } from "sequenceAnalyser/utilities/sequenceAnalyser/sequenceAnalyser";
import { ColorButton } from "widgets";

interface ComponentProps {
  features: SequenceFeatureWithRef[];
  featureDefinitions: FeatureDefinitions;
  onColorChange: (typeId: number, label: string, color: Color | undefined) => void;
}

const columns = {
  color: "Color",
  sequence: "Sequence",
  score: "Score",
  region: "Region",
  description: "Description",
  type: "Type",
  name: "Name",
};

type ColumnsKeys = keyof typeof columns;
type SortByDirection = undefined | "ASC" | "DESC";

interface SortBy {
  column: ColumnsKeys;
  direction: SortByDirection;
}

// See SEL11-847
function FeatureDefinitionTable({ features, featureDefinitions /*, onColorChange*/ }: ComponentProps) {
  const [columnHover, setColumnHover] = useState<ColumnsKeys | undefined>();
  const [page, setPage] = useState(0);
  const [pageSize, setPageSize] = useState(20);
  const [sortBy, setSortBy] = useState<SortBy | undefined>();

  const handleSortBy = (column: ColumnsKeys) => {
    if (sortBy && sortBy.direction === "DESC") {
      setSortBy(undefined);
    } else {
      // Direction in order: None > ASC > DESC
      let direction: SortByDirection;
      if (sortBy && sortBy.column !== column) {
        direction = "ASC";
      } else if (sortBy && sortBy.direction === "ASC") {
        direction = "DESC";
      } else if (!sortBy || !sortBy.direction) {
        direction = "ASC";
      }

      setSortBy({ column, direction });
    }
  };

  useEffect(() => {
    setPage(0);
  }, [sortBy]);

  let sortedFeatures = deepClone(features);

  if (sortBy) {
    sortedFeatures = sortedFeatures.sort((a, b) => {
      let aValue: string | undefined = "";
      let bValue: string | undefined = "";

      if (sortBy.column === "sequence") {
        aValue = a.sequenceName;
        bValue = b.sequenceName;
      } else if (sortBy.column === "region") {
        aValue = `${a.start}...${a.stop}`;
        bValue = `${b.start}...${b.stop}`;
      } else if (sortBy.column === "color") {
        aValue = featureDefinitions[buildFeatureId(a)].color.join("");
        bValue = featureDefinitions[buildFeatureId(b)].color.join("");
      } else if (["score", "description", "type", "name"].includes(sortBy.column)) {
        aValue = a[sortBy.column]?.toString();
        bValue = b[sortBy.column]?.toString();
      }

      aValue = aValue || "";
      bValue = bValue || "";

      if (sortBy.direction === "ASC") {
        return aValue.localeCompare(bValue);
      }
      return bValue.localeCompare(aValue);
    });
  }

  return (
    <>
      <Table size="small">
        <TableHead>
          <TableRow>
            {(Object.keys(columns) as ColumnsKeys[]).map((column) => (
              <TableCell
                key={column}
                onClick={() => handleSortBy(column)}
                onMouseEnter={() => setColumnHover(column)}
                onMouseLeave={() => setColumnHover(undefined)}
                style={{ cursor: "pointer", whiteSpace: "nowrap" }}
              >
                {columns[column]}
                {sortBy && sortBy.column === column ? (
                  <Box display="inline-block">
                    {sortBy.direction === "ASC" && <Icon element={ArrowUpwardIcon} color="action" />}
                    {sortBy.direction === "DESC" && <Icon element={ArrowDownwardIcon} color="action" />}
                  </Box>
                ) : (
                  <Box display="inline-block" visibility={columnHover === column ? "visible" : "hidden"}>
                    <Icon element={UnfoldMoreIcon} color="disabled" />
                  </Box>
                )}
              </TableCell>
            ))}
          </TableRow>
        </TableHead>
        <TableBody>
          {paginatedItems<SequenceFeatureWithRef>(page, pageSize, sortedFeatures).map((feature) => {
            //  See SEL11-847
            const featureDefinition = featureDefinitions[buildFeatureId(feature)];

            return (
              <TableRow key={feature.id}>
                {/* See SEL11-847 */}
                <TableCell>
                  <ColorButton isDisabled defaultColor={`rgb(${featureDefinition.color.join(",")})`} size={"tiny"} />
                </TableCell>
                <TableCell>{feature.sequenceName}</TableCell>
                <TableCell>{feature.score}</TableCell>
                <TableCell>{`${feature.start}...${feature.stop}`}</TableCell>
                <TableCell>
                  <Typography variant="body2">
                    <ExpandableText text={feature.description} maxLength={200} />
                  </Typography>
                </TableCell>
                <TableCell>{feature.type}</TableCell>
                <TableCell>{feature.name}</TableCell>
              </TableRow>
            );
          })}
        </TableBody>
      </Table>
      <Pagination
        count={features.length}
        page={page}
        pageSize={pageSize}
        handlePageChange={(newPage) => setPage(newPage)}
        handlePageSizeChange={(newPageSize) => {
          setPage(0);
          setPageSize(newPageSize);
        }}
        pageSizes={[10, 20, 50, 100]}
      />
    </>
  );
}

export default FeatureDefinitionTable;
